import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiCalendar, FiChevronUp, FiChevronDown, FiX } from "react-icons/fi";
import usePopoverPosition from "../../../hooks/usePopoverPosition";
// [R23] Nagłówek z listą miesięcy/lat, skokiem ±10 lat i wpisem daty z klawiatury — wspólny z DatePicker.
import NaglowekKalendarza from "./NaglowekKalendarza";
// [R22] Minuty spoza kwadransa zostają; strzałki idą po kwadransach; „Wpisz godzinę” daje każdą minutę.
import WpisGodziny, { KWADRANSE, minutaWGore, minutaWDol, najblizszyKwadrans } from "./WpisGodziny";
// [obszar 3, 2026-09-17] Granice lat 1910–2099 — węższe z (granica, min/max pola) wygrywa.
import { granice, ograniczRokWidoku } from "./GraniceDaty";
import styles from "./DatePicker.module.css";
import { useWarstwaNadModalem } from "../../SideDrawer/warstwaNadModalem";

const WEEKDAYS_PL = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Ndz"];

const MINUTE_OPTIONS = KWADRANSE;

/**
 * [BRIEF WŁAŚCICIELA 2026-09-10, B-9c] DOMYŚLNA GODZINA = TERAZ, W SIATCE CO 15 MINUT.
 *
 * Właściciel: „planowana data ok ale godzina przypisuj obecną z podziałem co 15 minut, a nie że
 * zawsze 9:00” — i wyraźnie: „wszędzie, nie tylko tam”.
 *
 * BYŁO ŹLE: `parseValue` przy pustej wartości oddawało na sztywno godzinę dziewiątą zero zero, więc
 * KAŻDE pole daty i godziny w aplikacji (planowana data zgłoszenia, terminy, przypomnienia)
 * podpowiadało 9:00 — także o 15:40. Poprawka siedzi w komponencie WSPÓLNYM, więc naprawia
 * wszystkie te miejsca naraz; żadne z nich nie ustawia własnej godziny startowej.
 *
 * Zaokrąglamy W GÓRĘ, nie do najbliższego kwadransa: „planowana” godzina, która wypadła przed
 * chwilą, jest z definicji bezużyteczna. 14:46 → 15:00, 14:44 → 14:45, 23:50 → 00:00 (data
 * jest osobnym polem, więc przewinięcie doby niczego nie psuje).
 */
function terazWKwadransie() {
    const t = new Date();
    const m = Math.ceil(t.getMinutes() / 15) * 15;
    if (m >= 60) return { hour: (t.getHours() + 1) % 24, minute: 0 };
    return { hour: t.getHours(), minute: m };
}
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

function pad(n) { return String(n).padStart(2, "0"); }

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
}

/**
 * Parse a value string into { dateStr, hour, minute }.
 * Accepts "YYYY-MM-DDTHH:mm" or "YYYY-MM-DD HH:mm" or ISO.
 */
function parseValue(val) {
    if (!val) return { dateStr: "", ...terazWKwadransie() };
    const d = new Date(val);
    if (isNaN(d.getTime())) return { dateStr: "", ...terazWKwadransie() };
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    // Wczytana minuta zostaje co do minuty (08:10 to 08:10, nie 08:15) — patrz WpisGodziny.js.
    // Siatka kwadransów dotyczy tylko DOMYŚLNEJ godziny pustego pola (terazWKwadransie) i przycisku „Teraz”.
    return { dateStr: `${y}-${m}-${dd}`, hour: d.getHours(), minute: d.getMinutes() };
}

function toDateStr(d) {
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${y}-${m}-${dd}`;
}

function formatDisplay(val) {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const datePart = d.toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" });
    return `${datePart}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DateTimePicker({
    value,
    onChange,
    placeholder = "Wybierz datę i godzinę",
    min,
    max,
    clearable = true,
    disabled = false,
    className = "",
    /* `nadModalem` — wybor warstwy kalendarza; opis reguły stoi przy DatePicker.js.
       Ten sam arkusz, ten sam defekt: pole w szufladzie nad oknem modalnym musi podniesc
       swoj popover na --z-drawer-over-modal-dropdown, bo --z-datepicker (10001) jest ponizej
       panelu szuflady (10011). Bez propsa decyduje kontekst WarstwaNadModalem (false na zwykłej stronie). */
    nadModalem: nadModalemJawny,
    /* Nazwa pola dla czytnika ekranu — reguła jak w DatePicker.ariaLabel: wyzwalacz to <button>, którego
       treścią jest sama wartość, więc bez etykiety nie wiadomo, CZEGO to data i godzina. */
    ariaLabel = null,
}) {
    const nadModalem = useWarstwaNadModalem(nadModalemJawny);
    const parsed = parseValue(value);
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => {
        if (parsed.dateStr) {
            const d = new Date(parsed.dateStr + "T00:00:00");
            // [obszar 3] Rok spoza granic (zrzut właściciela: 3423) nie wywozi widoku w rok, którego
            // lista lat nie zna — kalendarz siada na krawędzi zakresu.
            return isNaN(d.getTime()) ? new Date() : ograniczRokWidoku(d);
        }
        return new Date();
    });
    const [selDate, setSelDate] = useState(parsed.dateStr);
    const [hour, setHour] = useState(parsed.hour);
    const [minute, setMinute] = useState(parsed.minute);

    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    // Popover stoi PRZY polu (pomiar przed malowaniem) — patrz hooks/usePopoverPosition.
    const { style: popStyle } = usePopoverPosition(open, triggerRef, dropdownRef, { minHeight: 240, fit: true });

    // Sync internal state when value changes externally
    useEffect(() => {
        const p = parseValue(value);
        setSelDate(p.dateStr);
        setHour(p.hour);
        setMinute(p.minute);
        if (p.dateStr) {
            const d = new Date(p.dateStr + "T00:00:00");
            if (!isNaN(d.getTime())) setViewDate(ograniczRokWidoku(d));
        }
    }, [value]);

    const emitChange = useCallback((ds, h, m) => {
        if (!ds) { onChange(""); return; }
        onChange(`${ds}T${pad(h)}:${pad(m)}`);
    }, [onChange]);

    /* [zgłoszenie właściciela 2026-09-17] „zrób tak, że jak otwieram go, to domyślnie bierze dzisiejszą
       datę, ponieważ teraz jest tak, że jak wybiorę godzinę, to muszę wybrać datę, bo jak tego nie zrobię,
       nie wpisuje się nic."

       CO BYŁO ŹLE: `selectHour` i `selectMinute` oddają wartość tylko wtedy, gdy DZIEŃ jest już wybrany
       (`dzienWZakresie(selDate)`), a `emitChange` bez daty oddaje pusty napis. Przy pustym polu ustawienie
       samej godziny nie zapisywało więc NICZEGO — i nic tego nie tłumaczyło. Człowiek widział pokrętło,
       kręcił nim i zostawał z pustym polem.

       TERAZ otwarcie pustego pola podstawia DZISIAJ. Podstawienie jest WYŁĄCZNIE wewnętrzne: pole nadal
       pokazuje podpowiedź, dopóki ktoś czegoś nie wybierze — samo otwarcie i zamknięcie okna nie zapisuje
       daty, której nikt nie wskazał. Dopiero pierwszy wybór (dzień, godzina albo minuta) oddaje komplet.

       ⚠️ Gdy DZISIAJ leży poza `min`/`max` (np. pole „widoczne do" przy publikacji zaczynającej się
       w przyszłości), nie podstawiamy nic — inaczej pokrętło godziny oddawałoby dzień, którego ta sama
       kontrolka nie pozwala kliknąć. Reguła R22 zostaje w mocy. */
    const toggle = () => {
        if (disabled) return;
        const otwieramy = !open;
        if (otwieramy && !selDate) {
            const dzis = new Date();
            const ds = toDateStr(dzis);
            if (!isDayDisabled(new Date(ds + "T00:00:00"))) {
                setSelDate(ds);
                setViewDate(dzis);
            }
        }
        setOpen(otwieramy);
    };
    const close = () => setOpen(false);

    // [R22] min / max (np. „koniec publikacji nie przed jej początkiem”) — do tej pory przyjmowane i pomijane.
    // Porównujemy DZIEŃ ("RRRR-MM-DD" z "RRRR-MM-DDTHH:mm"), tak jak DatePicker.
    /* [obszar 3, 2026-09-17] …a zakres pola to PRZECIĘCIE tego min/max z granicami 1910–2099 (węższe
       wygrywa). To jedno miejsce zamyka WSZYSTKIE drogi: klik w dzień, „Teraz", „Zapisz", wpis daty
       z klawiatury i pokrętło godziny. Pokrętło jest tu najważniejsze: rekord zapisany kiedyś z rokiem
       3423 nie da się już „odświeżyć" samą zmianą godziny, bo dzień nie jest w zakresie. */
    const zakres = granice(min, max);
    const isDayDisabled = (d) => {
        const minD = new Date(zakres.min + "T00:00:00");
        if (d < minD && !isSameDay(d, minD)) return true;
        const maxD = new Date(zakres.max + "T00:00:00");
        if (d > maxD && !isSameDay(d, maxD)) return true;
        return false;
    };

    const selectDay = (day) => {
        if (isDayDisabled(day)) return;
        const ds = toDateStr(day);
        setSelDate(ds);
        emitChange(ds, hour, minute);
    };

    // [R23] Pełna data z klawiatury (dd.mm.rrrr): jak klik w dzień — godzina i minuty zostają te, które stoją.
    // Wpis NIE omija min/max (oddaje false → nagłówek pokazuje komunikat), jak w DatePicker.
    const wpisanaData = (ds) => {
        const wpisany = new Date(ds + "T00:00:00");
        setViewDate(ograniczRokWidoku(wpisany));
        if (isDayDisabled(wpisany)) return false;
        setSelDate(ds);
        emitChange(ds, hour, minute);
        return true;
    };

    // [R22] Pełna godzina z klawiatury (gg:mm) — każda minuta; wartość idzie do rodzica, gdy dzień jest wybrany.
    const wpisanaGodzina = (h, m) => {
        setHour(h);
        setMinute(m);
        if (dzienWZakresie(selDate)) emitChange(selDate, h, m);
    };

    const klawiszKalendarza = (e) => {
        if (e.key === "Escape") { e.stopPropagation(); close(); }
    };

    // [R22, poprawka po przeglądzie] Zmiana SAMEJ godziny oddaje wartość tylko na dniu w zakresie min/max.
    // Dzień spoza zakresu (np. wczytany, zanim ktoś przesunął min) trzeba najpierw zmienić klikiem albo wpisem.
    const dzienWZakresie = (ds) => !!ds && !isDayDisabled(new Date(ds + "T00:00:00"));

    const selectHour = (h) => {
        setHour(h);
        if (dzienWZakresie(selDate)) emitChange(selDate, h, minute);
    };

    const selectMinute = (m) => {
        setMinute(m);
        if (dzienWZakresie(selDate)) emitChange(selDate, hour, m);
    };

    const hourUp = (e) => { e.stopPropagation(); selectHour((hour + 1) % 24); };
    const hourDown = (e) => { e.stopPropagation(); selectHour((hour - 1 + 24) % 24); };
    const minuteUp = (e) => { e.stopPropagation(); selectMinute(minutaWGore(minute)); };
    const minuteDown = (e) => { e.stopPropagation(); selectMinute(minutaWDol(minute)); };

    const goNow = (e) => {
        e.stopPropagation();
        const now = new Date();
        const ds = toDateStr(now);
        const m = najblizszyKwadrans(now.getMinutes());
        setViewDate(now);
        // [R22, poprawka po przeglądzie] „Teraz” poza min/max (np. „Widoczne do” przy starcie publikacji w przyszłości)
        // tylko pokazuje bieżący miesiąc — nie oddaje dnia, którego nie da się kliknąć ani wpisać.
        if (!dzienWZakresie(ds)) return;
        setSelDate(ds);
        setHour(now.getHours());
        setMinute(m);
        emitChange(ds, now.getHours(), m);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange("");
        setSelDate("");
        close();
    };

    // Build calendar grid
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay();
    if (startDow === 0) startDow = 7;
    const today = new Date();

    const cells = [];
    const prevMonthLast = new Date(year, month, 0);
    for (let i = startDow - 2; i >= 0; i--) {
        cells.push({ date: new Date(year, month - 1, prevMonthLast.getDate() - i), otherMonth: true });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
        cells.push({ date: new Date(year, month, i), otherMonth: false });
    }
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
        cells.push({ date: new Date(year, month + 1, i), otherMonth: true });
    }

    const selectedDateObj = selDate ? new Date(selDate + "T00:00:00") : null;

    const dropdown = open ? createPortal(
        <>
            <div
                className={`${styles.overlay} ${nadModalem ? styles.overlayNadModalem : ""}`}
                onClick={close}
            />
            <div
                className={`${styles.dropdown} ${nadModalem ? styles.dropdownNadModalem : ""}`}
                ref={dropdownRef}
                style={popStyle}
                onClick={e => e.stopPropagation()}
                onKeyDown={klawiszKalendarza}
            >
                {/* Nagłówek: miesiąc / rok z list, ±10 lat, wpis daty z klawiatury (R23) */}
                <NaglowekKalendarza
                    viewDate={viewDate}
                    onViewDate={setViewDate}
                    dataStr={selDate}
                    onWpisanaData={wpisanaData}
                />

                {/* Weekday labels */}
                <div className={styles.weekdays}>
                    {WEEKDAYS_PL.map((wd, i) => (
                        <span
                            key={wd}
                            className={`${styles.weekday} ${i === 5 ? styles.weekdaySat : ""} ${i === 6 ? styles.weekdaySun : ""}`}
                        >
                            {wd}
                        </span>
                    ))}
                </div>

                {/* Day cells */}
                <div className={styles.days}>
                    {cells.map((c, i) => {
                        const isToday = isSameDay(c.date, today);
                        const isSelected = selectedDateObj && isSameDay(c.date, selectedDateObj);
                        const dow = c.date.getDay();
                        const isSat = dow === 6;
                        const isSun = dow === 0;
                        const dis = isDayDisabled(c.date);

                        return (
                            <button
                                key={i}
                                type="button"
                                className={[
                                    styles.day,
                                    c.otherMonth ? styles.dayOtherMonth : "",
                                    isToday ? styles.dayToday : "",
                                    isSelected ? styles.daySelected : "",
                                    isSat && !isSelected ? styles.daySat : "",
                                    isSun && !isSelected ? styles.daySun : "",
                                    dis ? styles.dayDisabled : "",
                                ].filter(Boolean).join(" ")}
                                onClick={() => !dis && selectDay(c.date)}
                                disabled={dis}
                            >
                                {c.date.getDate()}
                            </button>
                        );
                    })}
                </div>

                {/* Time Section */}
                <div className={styles.timeSection}>
                    <div className={styles.timeLabel}>Godzina</div>

                    <div className={styles.timeRow}>
                        {/* Hour spinner — przyciski dostały nazwy dla czytnika ekranu przy okazji
                            zgłoszenia z 17.09: były to cztery gołe ikony bez żadnego opisu. */}
                        {/* Hour spinner */}
                        <div className={styles.timeCol}>
                            <div className={styles.timeColLabel}>Godz.</div>
                            <div className={styles.timeScroll}>
                                <button className={styles.timeSpinBtn} type="button" onClick={hourUp} aria-label="Godzina w górę"><FiChevronUp /></button>
                                <div className={styles.timeValue}>{pad(hour)}</div>
                                <button className={styles.timeSpinBtn} type="button" onClick={hourDown} aria-label="Godzina w dół"><FiChevronDown /></button>
                            </div>
                        </div>

                        <span className={styles.timeSep}>:</span>

                        {/* Minute spinner */}
                        <div className={styles.timeCol}>
                            <div className={styles.timeColLabel}>Min.</div>
                            <div className={styles.timeScroll}>
                                <button className={styles.timeSpinBtn} type="button" onClick={minuteUp} aria-label="Minuty w górę"><FiChevronUp /></button>
                                <div className={styles.timeValue}>{pad(minute)}</div>
                                <button className={styles.timeSpinBtn} type="button" onClick={minuteDown} aria-label="Minuty w dół"><FiChevronDown /></button>
                            </div>
                        </div>
                    </div>

                    {/* [R22] Wpis godziny z klawiatury — każda minuta; Enter zapisuje i zamyka */}
                    <div className={styles.wpisGodzinyWiersz}>
                        <WpisGodziny
                            hour={hour}
                            minute={minute}
                            onWpisanaGodzina={wpisanaGodzina}
                            onZatwierdz={close}
                        />
                    </div>

                    {/* Quick hour grid */}
                    <div className={styles.hourGrid}>
                        {HOUR_OPTIONS.map(h => (
                            <button
                                key={h}
                                type="button"
                                className={`${styles.hourBtn} ${h === hour ? styles.hourBtnActive : ""}`}
                                onClick={() => selectHour(h)}
                            >
                                {pad(h)}
                            </button>
                        ))}
                    </div>

                    {/* Quick minute grid */}
                    <div className={styles.minuteGrid}>
                        {MINUTE_OPTIONS.map(m => (
                            <button
                                key={m}
                                type="button"
                                className={`${styles.minuteBtn} ${m === minute ? styles.minuteBtnActive : ""}`}
                                onClick={() => selectMinute(m)}
                            >
                                :{pad(m)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button className={styles.todayBtn} type="button" onClick={goNow}>
                        Teraz
                    </button>
                    {clearable && value && (
                        <button className={styles.clearBtn} type="button" onClick={handleClear}>
                            Wyczyść
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.zapiszBtn}
                        onClick={(e) => { e.stopPropagation(); if (dzienWZakresie(selDate)) emitChange(selDate, hour, minute); close(); }}
                    >
                        Zapisz
                    </button>
                </div>
            </div>
        </>,
        document.body
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.trigger} ${open ? styles.triggerOpen : ""} ${className}`}
                onClick={toggle}
                disabled={disabled}
                aria-label={ariaLabel ? `${ariaLabel}: ${value ? formatDisplay(value) : placeholder}` : undefined}
            >
                <span className={styles.triggerIcon}><FiCalendar /></span>
                <span className={`${styles.triggerText} ${!value ? styles.triggerPlaceholder : ""}`}>
                    {value ? formatDisplay(value) : placeholder}
                </span>
                {clearable && value && (
                    <span className={styles.triggerClear} onClick={handleClear} role="button" tabIndex={-1}>
                        <FiX />
                    </span>
                )}
            </button>
            {dropdown}
        </>
    );
}
